import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, verify session via httpOnly cookie (silent fail for anonymous users)
  useEffect(() => {
    authAPI.getMe()
      .then((res) => setUser(res.data))
      .catch(() => {/* Expected for anonymous visitors */})
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (data) => {
    const response = await authAPI.register(data);
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch {
      // Ignore errors on logout
    }
    setUser(null);
  }, []);

  const isAdmin = useCallback(() => user?.role === 'admin', [user]);
  const isClient = useCallback(() => user?.role === 'client', [user]);
  const isAuthenticated = useCallback(() => !!user, [user]);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    isAdmin,
    isClient,
    isAuthenticated,
  }), [user, loading, login, register, logout, isAdmin, isClient, isAuthenticated]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
